import gql from "graphql-tag";
import client from "../apolloClient";
import { refreshGlobalSubjects } from "./studentSubjects";
import { primeNotifications } from "./notificationsStore";
import { primeStudentGlobals } from "./studentGlobals";

/**
 * The per-exam data a login loads, in one place.
 *
 * After login the loading screen fetches these and stores them in
 * localStorage, where most pages read them from (studentglobals ~150 reads,
 * isStudentUserValid ~260, globalsubjects ~130). An exam switch gives the
 * student a different enrollment, so it must load the same set again.
 */

export const FETCH_STUDENTGLOBALS = gql`
  query($student_user_exam_id: Int!) {
    studentGlobals(student_user_exam_id: $student_user_exam_id) {
      loginRedirectionPath
      exams {
        id
        exam
        exam_subjects{
          subject_id
        }
      }
      classes {
        id
        class
      }
      questionTypes {
        id
        questiontype
      }
      complexity {
        id
        complexity
      }
      questionTheory {
        id
        question_theory
      }
      previousSets {
        id
        year
        qset
        exam
        pexamtype
        enabled
        attempted
        mains_2021
        exam_date
        examgroup
        exam_duration_min
        prevAnalyEnabled
        revChaptersEnabled
        linkAnalChaptersEnabled
        errorExamChaptersEnabled
      }
      previousYears {
        year
      }
      appModules{
        id
        title
      }
      uid
      exam_time
      tags{
        id
        tag
        type
      }
    }
  }
`;

export const FETCH_ISSTUDENTUSERVALID = gql`
  query ($student_user_exam_id: Int!) {
    isStudentUserValid(student_user_exam_id: $student_user_exam_id) {
      estatus
      current_plan_id
      expiry_date
      app_version
      user_access_restictions
      module_restrictions
    }
  }
`;

/** Store the login data where the pages read it from. */
export function saveStudentSession({ studentGlobals, isStudentUserValid }) {
  if (studentGlobals) localStorage.setItem("studentglobals", JSON.stringify(studentGlobals));
  if (isStudentUserValid) localStorage.setItem("isStudentUserValid", JSON.stringify(isStudentUserValid));
}

/**
 * Fetch and store everything a login loads for `studentUserExamId`: globals,
 * access restrictions, subjects, notifications.
 *
 * @returns {Promise<boolean>} true when the globals and access data (which
 * pages cannot work without) were stored; never rejects.
 */
export async function loadStudentSession(studentUserExamId) {
  const variables = { student_user_exam_id: parseInt(studentUserExamId) };
  const fetch = (query) => client
    .query({ query, variables, fetchPolicy: "network-only" })
    .then(r => r.data)
    .catch(error => { console.error("session load failed", error); return null; });

  const [globals, valid] = await Promise.all([
    fetch(FETCH_STUDENTGLOBALS),
    fetch(FETCH_ISSTUDENTUSERVALID),
    refreshGlobalSubjects(studentUserExamId),
  ]);

  saveStudentSession({
    studentGlobals: globals && globals.studentGlobals,
    isStudentUserValid: valid && valid.isStudentUserValid,
  });
  // As login does: warm the shared notification and globals caches.
  primeNotifications(true);
  primeStudentGlobals();

  return !!(globals && globals.studentGlobals && valid && valid.isStudentUserValid);
}
